import { DirectionProvider } from '@radix-ui/react-direction';
import { createContext, ReactNode, useContext } from 'react';
import { IntlProvider } from 'react-intl';
import { TBranding, TUser } from '../api';
import { useBidiSupport, useBranding, useLanguages, useUser } from '../hooks';

type TAppContext = {
  branding?: TBranding;
  user?: TUser;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
};

export const AppContext = createContext<TAppContext | null>(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('AppContext: No value provided');
  }

  return context;
};

type EnabledContexts = 'user' | 'branding' | 'language';

export const AppContextProviders = ({
  children,
  locale,
  brandingId,
  targetElementQuery,
  bundles,
  contexts = ['user', 'branding', 'language'],
  devLanguageKeys,
}: {
  children: ReactNode;
  locale?: string;
  brandingId?: number;
  bundles: string[];
  targetElementQuery: string;
  contexts?: EnabledContexts[];
  devLanguageKeys?: Record<string, string> | undefined;
}) => {
  const {
    data: branding,
    isLoading: _isLoadingBranding,
    isFetched: isBrandingFetched,
    isError: isBrandingError,
    error: brandingError,
  } = useBranding({
    brandingId: brandingId as number,
    enabled: contexts.includes('branding'),
  });
  const isLoadingBranding =
    contexts.includes('branding') && _isLoadingBranding && !isBrandingFetched;

  const {
    keys: languageKeys,
    isLoading: _isLoadingLanguage,
    isFetched: isLanguageFetched,
    isError: isLanguageError,
    error: languageError,
  } = useLanguages({
    locale,
    enabled: contexts.includes('language') && !!locale,
    bundles,
  });
  const isLoadingLanguage =
    contexts.includes('language') && _isLoadingLanguage && !isLanguageFetched;

  const {
    data: user,
    isLoading: _isUserLoading,
    isFetched: isUserFetched,
    isError: isUserError,
    error: userError,
  } = useUser({ enabled: contexts.includes('user') });
  const isLoadingUser =
    contexts.includes('user') && _isUserLoading && !isUserFetched;

  const { dir } = useBidiSupport({
    locale,
    elementToUpdate: targetElementQuery,
  });

  const isLoading = isLoadingBranding || isLoadingLanguage || isLoadingUser;
  const isError = isBrandingError || isLanguageError || isUserError;
  const error = brandingError || languageError || userError;

  return (
    <AppContext.Provider
      value={{ user, branding, isLoading, isError, error: error || undefined }}
    >
      <IntlProvider
        fallbackOnEmptyString={true}
        locale={locale || 'en'}
        messages={{
          ...(devLanguageKeys || {}),
          ...languageKeys,
        }}
      >
        <DirectionProvider dir={dir}>{children}</DirectionProvider>
      </IntlProvider>
    </AppContext.Provider>
  );
};
